import React from 'react';
import {
  Package, UtensilsCrossed, Users, DollarSign, Clock, CheckCircle, ChefHat, Truck,
  TrendingUp, Plus, Edit, Trash2, GripVertical, ArrowUp, ArrowDown, FolderOpen,
  Upload, Image, Star, MessageSquare, Mail, Eye, Reply, Send, Megaphone, Tag, Calendar, X, ChevronLeft
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { apiRequest, formatPrice, getAuthToken } from '../../lib/utils';

const PromotionsTab = ({
  deletePromotion,
  editingPromotion,
  handlePromotionImageUpload,
  handlePromotionSubmit,
  isPromotionDialogOpen,
  openEditPromotionDialog,
  promotionForm,
  promotionImageRef,
  promotions,
  resetPromotionForm,
  setIsPromotionDialogOpen,
  setPromotionForm,
  togglePromotionActive,
  uploadingPromotionImage,
}) => (
          <TabsContent value="promotions">
            <div className="space-y-6">
              <Card className="bg-[#141417] border-[#27272A]">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Megaphone className="h-5 w-5 text-[#FFC107]" />
                    Promotions Popup
                  </CardTitle>
                  <Dialog open={isPromotionDialogOpen} onOpenChange={setIsPromotionDialogOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        className="bg-[#FFC107] text-black hover:bg-[#F59E0B]"
                        onClick={() => resetPromotionForm()}
                        data-testid="add-promotion-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Promotion
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#141417] border-[#27272A] max-w-md">
                      <DialogHeader>
                        <DialogTitle className="text-white">
                          {editingPromotion ? 'Edit Promotion' : 'Add Promotion'}
                        </DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handlePromotionSubmit} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Title *</Label>
                          <Input
                            value={promotionForm.title}
                            onChange={(e) => setPromotionForm({...promotionForm, title: e.target.value})}
                            placeholder="e.g., 10% Off This Week!"
                            className="bg-[#0B0B0C] border-[#27272A]"
                            required
                            data-testid="promotion-form-title"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Description *</Label>
                          <Textarea
                            value={promotionForm.description}
                            onChange={(e) => setPromotionForm({...promotionForm, description: e.target.value})}
                            placeholder="Describe your promotion..."
                            className="bg-[#0B0B0C] border-[#27272A]"
                            required
                            data-testid="promotion-form-description"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Image</Label>
                          <input
                            type="file"
                            ref={promotionImageRef}
                            onChange={handlePromotionImageUpload}
                            accept="image/*"
                            className="hidden"
                          />
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => promotionImageRef.current?.click()}
                              disabled={uploadingPromotionImage}
                              className="flex-1 border-zinc-700 hover:bg-[#25252E]"
                            >
                              <Upload className="h-4 w-4 mr-2" />
                              {uploadingPromotionImage ? 'Uploading...' : 'Upload Image'}
                            </Button>
                          </div>
                          {promotionForm.image && (
                            <div className="mt-2">
                              <img 
                                src={promotionForm.image} 
                                alt="Preview" 
                                className="w-full h-32 object-cover rounded-lg"
                                onError={(e) => e.target.style.display = 'none'}
                              />
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Button Text</Label>
                            <Input
                              value={promotionForm.button_text}
                              onChange={(e) => setPromotionForm({...promotionForm, button_text: e.target.value})}
                              placeholder="Order Now"
                              className="bg-[#0B0B0C] border-[#27272A]"
                              data-testid="promotion-form-button-text"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Button Link</Label>
                            <Input
                              value={promotionForm.button_link}
                              onChange={(e) => setPromotionForm({...promotionForm, button_link: e.target.value})}
                              placeholder="/menu"
                              className="bg-[#0B0B0C] border-[#27272A]"
                              data-testid="promotion-form-button-link"
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <Label>Active (Show popup to visitors)</Label>
                          <Switch
                            checked={promotionForm.is_active}
                            onCheckedChange={(checked) => setPromotionForm({...promotionForm, is_active: checked})}
                            data-testid="promotion-form-active"
                          />
                        </div>
                        <Button type="submit" className="w-full bg-[#FFC107] text-black hover:bg-[#F59E0B]" data-testid="promotion-form-submit">
                          {editingPromotion ? 'Update Promotion' : 'Create Promotion'}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  <p className="text-zinc-400 text-sm mb-6">
                    Create promotions that appear as a popup when visitors open your website. Only one promotion can be active at a time.
                  </p>
                  
                  {promotions.length === 0 ? (
                    <div className="text-center py-12">
                      <Megaphone className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
                      <p className="text-zinc-500">No promotions yet</p>
                      <p className="text-zinc-600 text-sm">Create a promotion to show visitors when they open your website</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {promotions.map((promo) => (
                        <div
                          key={promo.id}
                          className={`bg-[#0B0B0C] rounded-lg p-4 border ${
                            promo.is_active ? 'border-green-500/50' : 'border-[#27272A]'
                          }`}
                          data-testid={`promotion-${promo.id}`}
                        >
                          <div className="flex items-start gap-4">
                            {promo.image && (
                              <img 
                                src={promo.image} 
                                alt={promo.title}
                                className="w-24 h-16 object-cover rounded-lg"
                              />
                            )}
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold text-white">{promo.title}</h4>
                                {promo.is_active ? (
                                  <Badge className="bg-green-500/20 text-green-500">Active</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-zinc-500 border-zinc-600">Inactive</Badge>
                                )}
                              </div>
                              <p className="text-sm text-zinc-400 line-clamp-2">{promo.description}</p>
                              <p className="text-xs text-zinc-600 mt-2">
                                Button: "{promo.button_text}" → {promo.button_link}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant={promo.is_active ? "outline" : "default"}
                                size="sm"
                                className={promo.is_active 
                                  ? "border-zinc-600 text-zinc-400 hover:bg-[#25252E] hover:text-white" 
                                  : "bg-green-600 hover:bg-green-700 text-white"
                                }
                                onClick={() => togglePromotionActive(promo)}
                                data-testid={`toggle-promotion-${promo.id}`}
                              >
                                {promo.is_active ? 'Deactivate' : 'Activate'}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-zinc-400 hover:text-white hover:bg-[#25252E] h-8 w-8"
                                onClick={() => openEditPromotionDialog(promo)}
                                data-testid={`edit-promotion-${promo.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8"
                                onClick={() => deletePromotion(promo.id)}
                                data-testid={`delete-promotion-${promo.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
);

export default PromotionsTab;
